import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Stat, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Plus, Scale, Clock, Sparkles, FileText, Handshake } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return s;
  }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="label-eyebrow">Loading…</div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={`Bienvenue, ${user?.full_name?.split(" ")[0] || ""}`}
        title="Your tribunal, at a glance."
        description="A quiet dashboard. A deliberate workspace. Everything your matter requires, in order."
        action={
          <Link to="/app/cases/new">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="dashboard-new-case-btn">
              <Plus size={14} className="mr-1.5" /> New case
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <Stat label="Cases" value={data?.case_count ?? 0} testId="stat-cases" />
        <Stat label="Active" value={data?.active_cases ?? 0} testId="stat-active" />
        <Stat label="Agreements" value={data?.agreement_count ?? 0} testId="stat-agreements" />
        <Stat label="Drafts" value={data?.draft_count ?? 0} testId="stat-drafts" />
        <Stat label="Open Tasks" value={data?.open_tasks ?? 0} testId="stat-tasks" />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Recent cases */}
        <div className="md:col-span-2 border border-[#E8E3F0] bg-white">
          <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
            <div className="label-eyebrow">Recent cases</div>
            <Link to="/app/cases" className="text-xs hover:text-[#C5A059]">View all</Link>
          </div>
          {data?.recent_cases?.length ? (
            <ul className="divide-y divide-[#E8E3F0]">
              {data.recent_cases.map((c) => (
                <li key={c.id} className="px-6 py-4 hover:bg-[#FAFAF8]" data-testid={`recent-case-${c.id}`}>
                  <Link to={`/app/cases/${c.id}`} className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-serif text-lg truncate">{c.title}</div>
                      <div className="text-xs text-[#4A5568] mt-0.5">
                        {c.case_type} · {c.jurisdiction}
                      </div>
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#4A5568]">
                      {c.status}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-10">
              <EmptyState
                icon={Scale}
                title="No cases yet"
                description="Open your first case to begin structuring the record."
                action={
                  <Link to="/app/cases/new">
                    <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="empty-new-case-btn">
                      <Plus size={14} className="mr-1.5" /> New case
                    </Button>
                  </Link>
                }
              />
            </div>
          )}
        </div>

        {/* Upcoming tasks */}
        <div className="border border-[#E8E3F0] bg-white">
          <div className="px-6 py-4 border-b border-[#E8E3F0]">
            <div className="label-eyebrow">Upcoming deadlines</div>
          </div>
          {data?.upcoming_tasks?.length ? (
            <ul className="divide-y divide-[#E8E3F0]">
              {data.upcoming_tasks.map((t) => (
                <li key={t.id} className="px-6 py-4" data-testid={`upcoming-task-${t.id}`}>
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-xs text-[#4A5568] mt-0.5 flex items-center gap-1.5">
                    <Clock size={12} /> {fmtDate(t.due_date)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-sm text-[#4A5568]">Nothing urgent. Breathe.</div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-10 grid md:grid-cols-4 gap-px bg-[#E8E3F0] border border-[#E8E3F0]">
        {[
          { to: "/app/templates", label: "Browse Templates", icon: FileText, testId: "quick-templates" },
          { to: "/app/assistant", label: "Ask Marianne (AI)", icon: Sparkles, testId: "quick-ai" },
          { to: "/app/agreements", label: "Agreements", icon: Handshake, testId: "quick-agreements" },
          { to: "/app/education", label: "Education", icon: FileText, testId: "quick-education" },
        ].map((a) => {
          const I = a.icon;
          return (
            <Link key={a.to} to={a.to} className="bg-white p-6 hover-lift" data-testid={a.testId}>
              <I size={18} strokeWidth={1.5} className="text-[#C5A059] mb-3" />
              <div className="font-serif text-xl">{a.label}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
